import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import MetaDataMarkup from 'components/MetaDataMarkup';
import ThemePage from 'views/ThemePage';
import { addChatInteractionListener, removeChatInteractionListener } from 'helpers/hubspot';
import measureNavigation from 'helpers/measureNavigation';
import waitForSaga from 'helpers/waitForSaga';
import themePageViewProps from 'helpers/pageViewProps/theme';
import { themePageLevelBaseProps } from 'helpers/themes';
import {
  getTheme,
  getThemeCount,
  selectThemeCount,
  selectThemeCountErrorContent,
  selectThemeCountHasError,
  selectThemes,
  selectThemesErrorContent,
  selectThemesHasError,
} from 'state/modules/themes';
import { hubspotChatInitiation } from 'state/modules/analytics';
import { getThemeHeaderImages } from 'views/ThemePage/ThemeHeader';
import buildPageAnalytics from 'helpers/buildPageAnalytics';
import { INDIVIDUAL_THEME_PAGE_NAME } from 'constants/pageNames';
import configureStore from 'state/configureStore';
import getServerSidePropsWrapper from 'helpers/getServerSidePropsWrapper';

export class Theme extends Component {
  componentDidMount() {
    this.props.dispatch(getThemeCount());
    measureNavigation();
    addChatInteractionListener(this.trackHubspotChatInitiation);
    if (window.initPinterestWidgets) window.initPinterestWidgets();
  }
  componentWillUnmount() {
    removeChatInteractionListener(this.trackHubspotChatInitiation);
  }

  trackHubspotChatInitiation = () => {
    const { dispatch } = this.props;
    dispatch(hubspotChatInitiation('theme'));
  };

  render() {
    const { theme, themeCount } = this.props;

    const { title, metaTitle, metaDescription, slug, image } = theme;

    const openGraphProps = {
      description: `Search food, drink, decoration and entertainment ideas for putting together a ${title.toLowerCase()} party theme. Whether you're celebrating a birthday or anniversary, find the best party inspiration on The Bash.`,
      image: `${image.url}.webp?quality=50`,
      imageAlt: `${title} party themed inspiration and ideas`,
      title: metaTitle || `${title} Party Theme | The Bash`,
      type: 'article',
      url: `https://www.thebash.com/themes/${slug}`,
    };

    const imageUrls = getThemeHeaderImages(image.url);

    return (
      <>
        <MetaDataMarkup
          title={metaTitle || `${title} Party Theme`}
          description={metaDescription}
          openGraphProps={openGraphProps}
          twitterImageAlt={`${title} party themed inspiration and ideas`}
          mobileHeroImageUrl={`${imageUrls.mobile}?quality=50`}
          desktopHeroImageUrl={`${imageUrls.desktop}?quality=50`}
          includePinterestScript
        />
        <ThemePage theme={theme} themeCount={themeCount} />
      </>
    );
  }
}

export const handleServerSideProps = async (ctx) => {
  const { query, store } = ctx;
  const { themeSlug } = query;
  let errorCode;

  store.dispatch(getTheme(themeSlug));

  await waitForSaga(store);

  const updatedState = store.getState();

  if (selectThemeCountHasError(updatedState)) {
    errorCode = selectThemeCountErrorContent(updatedState);
  }

  if (selectThemesHasError(themeSlug)(updatedState)) {
    errorCode = selectThemesErrorContent(themeSlug)(updatedState);
  }

  const theme = selectThemes(themeSlug)(updatedState);
  if (theme === null) errorCode = 404;

  const pageAnalytics = buildPageAnalytics(
    INDIVIDUAL_THEME_PAGE_NAME,
    themePageViewProps(selectThemes(themeSlug)(updatedState)),
    themePageLevelBaseProps(theme)
  );

  return {
    ...pageAnalytics,
    themeSlug,
    errorCode,
    theme,
  };
};

export const getServerSideProps = configureStore.getServerSideProps(
  (store) => async (ctx) =>
    await getServerSidePropsWrapper({ ...ctx, store }, handleServerSideProps)
);

Theme.propTypes = {
  theme: PropTypes.shape({}),
  themeCount: PropTypes.number,
  dispatch: PropTypes.func.isRequired,
};

Theme.defaultProps = {
  theme: {},
  themeCount: 0,
};

const mapStateToProps = (state, ownProps) => {
  if (ownProps.errorCode) return {};

  return {
    themeCount: selectThemeCount(state),
  };
};

export default connect(mapStateToProps)(Theme);
